import { useEffect, useState } from "react";

type SafeAreaInsets = { top: number; bottom: number };

function readSafeAreaInsets(): SafeAreaInsets {
  // `document.body` can be null when a resize/orientationchange fires while the
  // document is being torn down (e.g. navigating away); nothing to measure then.
  const body = document.body;
  if (!body) {
    return { top: 0, bottom: 0 };
  }

  const probe = document.createElement("div");
  probe.style.cssText =
    "position:fixed;visibility:hidden;pointer-events:none;" +
    "padding-top:var(--safe-area-top,0px);" +
    "padding-bottom:var(--safe-area-bottom,0px);";
  body.appendChild(probe);

  const computed = getComputedStyle(probe);
  const insets = {
    top: parseFloat(computed.paddingTop) || 0,
    bottom: parseFloat(computed.paddingBottom) || 0,
  };

  probe.remove();

  return insets;
}

/**
 * Resolved device safe-area insets in px (0 in regular browsers). For
 * fixed-positioned overlays whose offsets are computed in JS (e.g. HeroUI
 * toasts) — they ignore the body's safe-area padding, and `env()` isn't
 * readable from JS, so this measures the resolved `--safe-area-*` vars
 * (defined in index.css) and re-measures on resize/rotation.
 */
export function useSafeAreaInsets(): SafeAreaInsets {
  const [insets, setInsets] = useState<SafeAreaInsets>(readSafeAreaInsets);

  useEffect(() => {
    const update = () => {
      const next = readSafeAreaInsets();
      setInsets((previous) =>
        previous.top === next.top && previous.bottom === next.bottom
          ? previous
          : next,
      );
    };

    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, []);

  return insets;
}
